import React, { useState } from 'react';
import { Table, Button, InputNumber, message } from 'antd';
import { CheckOutlined, CloseOutlined, DeleteOutlined, EditOutlined } from '@ant-design/icons';

interface ImportItem {
  key?: string;
  departmentLabel: string;
  department_id?: number;
  product_category: string;
  product?: string;
  product_variety?: string | null;
  qty: number;
  uom: string;
  unit_price?: number;
  lcl?: number;
  fcl?: number;
  serviceCharge?: string | null;
  total?: number;
}

interface ImportItemTableProps {
  data: ImportItem[];
  onRemove?: (key: string) => void;
  editable?: boolean;
  type?: 'edit' | 'create' | 'view';
  onQtyChange?: (key: string, qty: number) => Promise<void>;
  handleFieldChange?: (key: string, field: 'lcl' | 'fcl', value: number) => void;
}

const ImportItemTable: React.FC<ImportItemTableProps> = ({
  data,
  onRemove,
  onQtyChange,
  editable,
  type,
  handleFieldChange,
}) => {
  const [editingQty, setEditingQty] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<number>(0);
  const [loading, setLoading] = useState<string | null>(null);

  const dataWithKeys = data.map((item, index) => ({
    ...item,
    key: item.key || `item-${index}`,
  }));

  const harbourItems = dataWithKeys.filter((item) => item.department_id === 6);
  const otherItems = dataWithKeys.filter((item) => item.department_id !== 6);

  const handleQtyEditStart = (key: string, currentQty: number) => {
    setEditingQty(key);
    setEditValue(currentQty);
  };

  const handleQtyEditCancel = () => {
    setEditingQty(null);
    setEditValue(0);
  };

  const handleQtyEditSave = async () => {
    if (!editingQty || !onQtyChange) return;

    if (editValue <= 0) {
      message.error('Quantity must be greater than 0');
      return;
    }

    try {
      setLoading(editingQty);
      await onQtyChange(editingQty, editValue);
      setEditingQty(null);
      setEditValue(0);
      message.success('Quantity updated successfully');
    } catch (error) {
      message.error('Failed to update quantity. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const renderQtyField = (qty: number, record: ImportItem) => {
    const isEditing = editingQty === record.key;
    const isLoading = loading === record.key;

    if (editable && type !== 'view') {
      if (isEditing) {
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <InputNumber
              min={1}
              value={editValue}
              onChange={(newValue) => setEditValue(newValue || 0)}
              style={{ width: 80 }}
              size="small"
              disabled={isLoading}
            />
            <Button
              type="text"
              icon={<CheckOutlined />}
              size="small"
              onClick={handleQtyEditSave}
              loading={isLoading}
              style={{ color: '#52c41a' }}
            />
            <Button
              type="text"
              icon={<CloseOutlined />}
              size="small"
              onClick={handleQtyEditCancel}
              disabled={isLoading}
              style={{ color: '#ff4d4f' }}
            />
          </div>
        );
      }

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <span>{qty}</span>
          <Button
            type="text"
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleQtyEditStart(record.key!, qty)}
            style={{ color: '#1890ff' }}
          />
        </div>
      );
    }

    return qty;
  };

  const productBaseColumns = [
    {
      title: 'Department',
      dataIndex: 'departmentLabel',
      width: '15%',
    },
    {
      title: 'Category',
      dataIndex: 'product_category',
      render: (_: any, record: ImportItem) => record.product_category || '-',
    },
    {
      title: 'Species',
      dataIndex: 'product',
      render: (_: any, record: ImportItem) => record.product || '-',
    },
    {
      title: 'Variety',
      dataIndex: 'product_variety',
      render: (_: any, record: ImportItem) => record.product_variety || '-',
    },
  ];

  const harbourBaseColumns = [
    {
      title: 'Department',
      dataIndex: 'departmentLabel',
      width: '15%',
    },
    {
      title: 'Category',
      dataIndex: 'product_category',
      render: (_: any, record: ImportItem) => record.product_category || '-',
    },
  ];

  const extraHarbourColumns = [
    {
      title: 'Service Charge',
      dataIndex: 'serviceCharge',
      render: (value: string) => value ?? '-',
    },
    {
      title: 'LCL',
      dataIndex: 'lcl',
      render: (value: number, record: ImportItem) => {
        if (editable && type !== 'view') {
          return (
            <InputNumber
              min={0}
              value={record.lcl ?? 0}
              onChange={(val) => {
                handleFieldChange && handleFieldChange(record.key!, 'lcl', Number(val) || 0);
              }}
              style={{ width: 80 }}
              size="small"
            />
          );
        }
        return value ?? '-';
      },
    },
    {
      title: 'FCL',
      dataIndex: 'fcl',
      render: (value: number, record: ImportItem) => {
        if (editable && type !== 'view') {
          return (
            <InputNumber
              min={0}
              value={record.fcl ?? 0}
              onChange={(val) => {
                handleFieldChange && handleFieldChange(record.key!, 'fcl', Number(val) || 0);
              }}
              style={{ width: 80 }}
              size="small"
            />
          );
        }
        return value ?? '-';
      },
    },
  ];

  const sharedColumns = [
    {
      title: 'QTY',
      dataIndex: 'qty',
      render: (value: number, record: ImportItem) => renderQtyField(value, record),
    },
    {
      title: 'UOM',
      dataIndex: 'uom',
      render: (value: string, record: ImportItem) => {
        const isCurrentlyLoading = loading === record.key;
        return isCurrentlyLoading ? 'Updating...' : value;
      },
    },
    {
      title: 'Unit Price',
      dataIndex: 'unit_price',
      render: (_: any, record: ImportItem) => {
        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';

        console.log('Rendering unit price for record:', record);

        const price = record.unit_price;

        // Handle both 0 and undefined/null values
        if (price === undefined || price === null) {
          return '-';
        }

        return `LKR ${price.toLocaleString()}/=`;
      },
    },
    {
      title: 'Subtotal',
      dataIndex: 'total',
      render: (_: any, record: ImportItem) => {
        const isCurrentlyLoading = loading === record.key;
        if (isCurrentlyLoading) return 'Updating...';

        let subtotal = record.total;

        if (subtotal === undefined || subtotal === null) {
          const price = record.unit_price || 0;
          subtotal = price * record.qty;
        }

        console.log('Subtotal value:', subtotal);

        return `LKR ${subtotal.toLocaleString()}/=`;
      },
    },
    ...(editable
      ? [
          {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: any) =>
              record.isHeader ? (
                { props: { colSpan: 0 } }
              ) : (
                <Button danger icon={<DeleteOutlined />} size="small" onClick={() => onRemove?.(record.key)} />
              ),
          },
        ]
      : []),
  ];

  return (
    <>
      {otherItems.length > 0 && (
        <>
          <h3>Products</h3>
          <Table
            dataSource={otherItems}
            columns={[...productBaseColumns, ...sharedColumns]}
            pagination={false}
            rowKey="key"
            size="small"
            bordered
            style={{ marginBottom: 32 }}
          />
        </>
      )}

      {harbourItems.length > 0 && (
        <>
          <h3>Services</h3>
          <Table
            dataSource={harbourItems}
            columns={[...harbourBaseColumns, ...extraHarbourColumns, ...sharedColumns]}
            pagination={false}
            rowKey="key"
            size="small"
            bordered
          />
        </>
      )}
    </>
  );
};

export default ImportItemTable;
